import { createApi } from '@reduxjs/toolkit/query/react';
import { idType, selectItemType } from '@src/types';
import { baseQuerySample } from '@store/helpers';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';

export interface getContractsByIdResponseType {
  document_id?: number;
  effectiveDate?: string;
  executionDate?: string;
  expirationDate?: string;
  id?: number;
  payor_id?: number;
  shortNotes?: string;
  rate?: string;
  status?: selectItemType | null;
  type?: selectItemType | null;
}

export const agreementsApi = createApi({
  reducerPath: 'Agreements',
  tagTypes: ['Agreements'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getContractAgreements: builder.mutation({
      query: ({ path, body = {}, params = {} }) => ({
        url: `/v1/${path}`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    createContractAgreements: builder.mutation({
      query: (data) => ({
        url: '/v1/company/contracts/create',
        method: 'POST',
        body: data,
      }),
    }),
    getAgreementsStatus: builder.query({
      query: () => ({
        url: '/v1/company/status',
      }),
    }),
    getContractAgreementsById: builder.query<
      getContractsByIdResponseType,
      { id?: idType; path?: string }
    >({
      query: ({ id, path = '' }) => ({
        url: `/v1/${path}/contracts/${id}`,
      }),
    }),
    createOrUpdateContractAgreement: builder.mutation({
      query: ({ id, data, path, method }) => ({
        url: `/v1/${path}/contracts/${id ? `update/${id}` : 'create'}`,
        method: method,
        body: data,
      }),
    }),
    getFactoringAgreement: builder.mutation({
      query: ({ body = {}, params = {} }) => ({
        url: `/v1/contractors/factoring-agreements/contractor`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getFactoringAgreementById: builder.query<
      getContractsByIdResponseType,
      { id?: idType }
    >({
      query: ({ id }) => ({
        url: `/v1/contractors/factoring-agreements/${id}`,
      }),
    }),
    createOrUpdateFactoringAgreement: builder.mutation({
      query: ({ id, data, method }) => ({
        url: `/v1/contractors/factoring-agreements/${id ? `update/${id}` : 'create'}`,
        method: method,
        body: data,
      }),
    }),
  }),
});

export const {
  useCreateOrUpdateContractAgreementMutation,
  useGetContractAgreementsMutation,
  useCreateContractAgreementsMutation,
  useLazyGetContractAgreementsByIdQuery,
  useGetFactoringAgreementMutation,
  useLazyGetFactoringAgreementByIdQuery,
  useCreateOrUpdateFactoringAgreementMutation,
} = agreementsApi;
